/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useMemo} from 'react';
import {makeStyles, Theme} from '@material-ui/core/styles';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {ViewSubGroupsPosition} from '@ion/dls/src/types/types';
import {toggleThemeStyle} from '../../theme/helpers';
import Switch from './Switch';
import useSubAccountsToggleSetup from './useSubAccountsToggleSetup';
import useToggleSubGroupChange from './useToggleSubGroupChange';

interface StyleProps {
  isCtcAdminDashboard?: boolean;
  labelColor?: string;
};

export const useStyles = makeStyles<Theme, StyleProps>(() => ({
  container : {
    display        : 'flex',
    justifyContent : toggleThemeStyle(
      ({isCtcAdminDashboard}: StyleProps) => isCtcAdminDashboard ? 'left' : 'center',
      'flex-start'
    ),
    alignItems : 'center',
    width      : toggleThemeStyle('auto', '100%')
  },
  labelGridCtas : toggleThemeStyle({
    ...dlsTypography('h4'),
    color         : ({labelColor}: StyleProps) => labelColor,
    letterSpacing : '1px',
    marginRight   : '15px'
  }, {
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '20px',
    letterSpacing : '0px'
  }),
  labelGridPaginationControls : {
  }
}));

export interface ViewSubGroupsSwitchProps {
  checked: boolean;
  onChange?: Function;
  onChecked?: Function;
  disabled?: boolean;
  showLabel?: boolean;
  labelColor?: string;
  position?: ViewSubGroupsPosition;
  label?: string;
  preference?: string;
  isCtcAdminDashboard?: boolean;
};

/**
 * Defines the ViewSubGroupsSwitch component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * <ViewSubGroupsSwitch checked={viewSubGroups}
 *   onChange={toggleSubGroupsView}
 *   position={ViewSubGroupsPosition.GRID_CTAS} />
 *
 * ```
 */
export const ViewSubGroupsSwitch = ({
  checked,
  onChange,
  onChecked,
  label,
  preference = '',
  showLabel = false,
  labelColor = dlsColor('aaBlue'),
  disabled = false,
  isCtcAdminDashboard = false,
  position = ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS
}: ViewSubGroupsSwitchProps) => {
  const classes                                   = useStyles({isCtcAdminDashboard, labelColor});
  const isMounted                                 = useHasMounted();
  const {isDesktop}                               = useMedia();
  const {subAccountChecked, setSubAccountChecked} = useSubAccountsToggleSetup({preference, defaultValue: checked});
  const viewSubAccountLabel                       = label ?? translate('grids:pagination.viewSubgroups');
  const onToggleSubGroups = useToggleSubGroupChange({
    onChecked, onChange, setSubAccountChecked, preference
  });

  const labelParams = useMemo(() => ({
    [ViewSubGroupsPosition.GRID_CTAS] : {
      offset : '-8px',
      class  : classes.labelGridCtas
    },
    [ViewSubGroupsPosition.GRID_PAGINATION_CONTROLS] : {
      offset : '-0px',
      class  : classes.labelGridPaginationControls
    }
  }), [classes]);

  return (
    <div className={classes.container}>
      <div>
        <Switch checked={preference ? subAccountChecked : checked}
          disabled={!isMounted || disabled}
          marginRight={labelParams[position]?.offset}
          onChange={onToggleSubGroups} />
      </div>
      {(isDesktop || showLabel) && (
        <div className={labelParams[position]?.class}>
          {viewSubAccountLabel}
        </div>
      )}
    </div>
  );
};

export default memo(ViewSubGroupsSwitch);
